<template>
  <form-element
    v-bind="$props"
    class="dotcom-radio-group"
    :class="{ 'dotcom-radio-group--as-buttons': asButtons }"
  >
    <div class="dotcom-radio-group__label">
      <span v-if="titleLabel" class="dotcom-radio-group__title">
        {{ localizedTitle }}
      </span>
    </div>

    <div
      :class="{
        'dotcom-radio-group__items--horizontal': horizontal,
        'dotcom-radio-group__items--as-buttons': asButtons,
      }"
      class="dotcom-radio-group__items"
    >
      <template v-for="(item, i) in items">
        <radio
          :key="item.value"
          v-model="model"
          v-autofocus="i === 0 && shouldFocus"
          :name="radioName"
          :disabled="item.disabled"
          :input-value="item.value"
          :visually-hidden="asButtons"
          class="dotcom-radio-group__radio"
          :class="{
            'dotcom-radio-group__radio--selected': model === item.value,
            'dotcom-radio-group--error': !isEmpty(errors),
          }"
          @input="select(item.value)"
        >
          <span v-html="item.text" />
        </radio>
      </template>
    </div>
  </form-element>
</template>

<script>
import isEmpty from 'lodash/isEmpty'

import focusDirective from '../../directives/focus'
import FormElement from '../FormElement'
import Radio from './Radio'

export default {
  name: 'RadioGroup',

  components: {
    FormElement,
    Radio,
  },

  directives: {
    autofocus: focusDirective,
  },

  props: {
    titleLabel: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [Object, String, Number, Boolean],
      default: '',
    },
    items: {
      type: Array,
      required: true,
    },
    showLabel: Boolean,
    required: Boolean,
    errors: {
      type: Object,
      default: () => {
        return {}
      },
    },
    horizontal: Boolean,
    autofocus: Boolean,
    asButtons: Boolean,
    tooltipContent: {
      type: String,
      default: '',
    },

    tooltipLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      model: this.value,
      originalModel: this.value,
      isMobile: /Android|Mobi|iPad|iPhone/.test(navigator.userAgent),
    }
  },

  computed: {
    localizedTitle() {
      return `${this.titleLabel}${this.required ? ' *' : ''}`
    },

    radioName() {
      return this.name || 'radio' + this._uid
    },

    /**
     * autofocus is disabled completely on mobile!
     * @return {Boolean}
     */
    shouldFocus() {
      return this.autofocus && !this.isMobile
    },
  },

  watch: {
    value(val) {
      if (!this.originalModel) {
        this.originalModel = val
      }
      this.model = val
    },
  },

  methods: {
    isEmpty,
    select(value, isDisabled = false) {
      if (isDisabled === false) {
        this.model = value
        this.$emit('input', value)
      }
    },

    isOriginal(item) {
      return item.value === this.originalModel
    },
  },
}
</script>

<style lang="postcss" scoped>
.dotcom-radio-group {
  margin-bottom: 20px;
  display: inline-block;

  &--as-buttons {
    width: 100%;
  }
}

.dotcom-radio-group__label {
  position: relative;
}

.dotcom-radio-group__title {
  position: relative;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--c-primary-neutral-1);

  display: inline-flex;
  flex-direction: row;
  justify-content: right;
  margin-top: -5px;

  span {
    flex-direction: row;
  }
}

.dotcom-radio-group__items {
  display: flex;
  flex-direction: column;
  column-gap: 25px;
  row-gap: 16px;
}

/* Needs to be specific enough to override the default */
.dotcom-radio-group--error >>> svg.dotcom-icon {
  color: var(--c-secondary-color-1);
}

.dotcom-radio-group__items--horizontal {
  flex-direction: row;
  flex-wrap: wrap;
}

.dotcom-radio-group__items--as-buttons {
  display: grid;
  grid-gap: 15px;
  grid-template-columns: repeat(2, [col] minmax(0, 1fr));

  .dotcom-radio-group__radio {
    display: flex;
    overflow: hidden;

    border: 1px solid var(--c-primary-neutral-1);
    font-size: 15px;

    height: 50px;
    outline: none;
    cursor: pointer;
    transition: background-color 150ms ease-in-out, color 150ms ease-in-out,
      border 150ms ease-in-out, font-weight 150ms ease-in-out;
    font-weight: normal;

    padding-bottom: 0;

    & >>> div:nth-child(2) {
      max-width: 100%;

      span {
        max-width: 100%;
        transition: color 150ms ease-in-out;
      }
    }

    &--selected {
      font-weight: bold;
      border-color: var(--c-primary-color-2);
      background-color: var(--c-primary-color-2);
      color: var(--c-primary-neutral-3);
      cursor: default;

      /* more specific than sanitasForm styles */

      &:hover >>> div:nth-child(2) span {
        color: var(--c-primary-neutral-3);
      }
    }

    &:focus {
      /* outline: 2px dotted var(--c-primary-neutral-1); */
    }

    &:hover {
      border-color: var(--c-primary-color-2);

      & + .dotcom-radio-group__radio {
        border-left-color: var(--c-primary-color-2);
      }
    }
  }
}

@media (--md) {
  .dotcom-radio-group__items--as-buttons {
    grid-gap: 10px 0;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));

    .dotcom-radio-group__radio {
      border: 1px solid var(--c-primary-neutral-1);
      padding: 5px;
      margin-right: -1px;

      &:last-child {
        border-right: 1px solid var(--c-primary-neutral-1);
      }

      &--selected,
      &:hover {
        border-color: var(--c-primary-color-2);
        z-index: 1;
      }
    }
  }
}
</style>
